import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSyncAlt,
  faFilter,
  faSpinner,
  faEye,
  faFileCsv,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const sortOptions = [
  { value: "created_at", label: "Created At" },
  { value: "date_received", label: "Date Received" },
  { value: "loa_no", label: "LOA No" },
  { value: "trans_no", label: "Transaction No" },
];

const ClaimsReceivingExcludedLogs = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    trans_no: "",
    loa_no: "",
    provider_code: "",
    coverage_code: "",
    claim_status: "",
    uploaded_by: "",
    soa: "",
    reason: "",
  });
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const token = localStorage.getItem("authentication/token");

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${BASE_URL}/claims/receiving/excluded-logs`,
        {
          params: {
            page,
            per_page: perPage,
            sort_by: sortBy,
            sort_direction: sortDir,
            ...filters,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRows(response.data?.data || []);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total_records || 0);
    } catch (error) {
      // silent fail; page-level toasts optional
    } finally {
      setIsLoading(false);
    }
  };

  const exportLogs = async (format) => {
    const response = await axios.get(
      `${BASE_URL}/claims/receiving/excluded-logs/export`,
      {
        params: { format, ...filters },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type:
        format === "excel"
          ? "application/vnd.ms-excel"
          : "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date()
      .toISOString()
      .replace(/[:T]/g, "-")
      .split(".")[0];
    link.href = url;
    link.setAttribute(
      "download",
      `claims_receiving_excluded_logs_${timestamp}.${
        format === "excel" ? "xls" : "csv"
      }`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    fetchLogs();
  }, [page, perPage, sortBy, sortDir]);

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const applyFilters = () => {
    setPage(1);
    fetchLogs();
  };

  const resetFilters = () => {
    setFilters({
      date_from: "",
      date_to: "",
      trans_no: "",
      loa_no: "",
      provider_code: "",
      coverage_code: "",
      claim_status: "",
      uploaded_by: "",
      soa: "",
      reason: "",
    });
    setPage(1);
  };

  const openViewModal = (row) => {
    setSelectedRow(row);
    setIsModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedRow(null);
    setIsModalOpen(false);
  };

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const detailFields = [
    { label: "ID", key: "id" },
    { label: "Transaction No", key: "trans_no" },
    { label: "LOA No", key: "loa_no" },
    { label: "Provider Code", key: "provider_code" },
    { label: "Coverage Code", key: "coverage_code" },
    { label: "Claim Status", key: "claim_status" },
    { label: "SOA", key: "soa" },
    { label: "Date Received", key: "date_received" },
    { label: "Uploaded By", key: "uploaded_by" },
    { label: "Uploaded By ID", key: "uploaded_by_id" },
    { label: "Reason", key: "reason" },
    { label: "Created At", key: "created_at" },
    { label: "Updated At", key: "updated_at" },
  ];

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return value;
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Claims Receiving Excluded Logs (deprecated)
          </h1>
          <p className="text-sm text-gray-600">
            View receive-only LOAs excluded from batch creation.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchLogs}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" /> Refresh
          </button>
          <button
            onClick={() => exportLogs("csv")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileCsv} className="mr-2" /> CSV
          </button>
          <button
            onClick={() => exportLogs("excel")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" /> Excel
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600">Date From</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_from: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Date To</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, date_to: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Transaction No</label>
            <input
              value={filters.trans_no}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, trans_no: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Transaction no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">LOA No</label>
            <input
              value={filters.loa_no}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, loa_no: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="LOA no"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Provider Code</label>
            <input
              value={filters.provider_code}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, provider_code: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Provider code"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Coverage Code</label>
            <input
              value={filters.coverage_code}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, coverage_code: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Coverage code"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Claim Status</label>
            <input
              value={filters.claim_status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, claim_status: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Claim status"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Uploaded By</label>
            <input
              value={filters.uploaded_by}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, uploaded_by: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Receiver name"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">SOA</label>
            <input
              value={filters.soa}
              onChange={(e) => setFilters((prev) => ({ ...prev, soa: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="SOA"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Reason</label>
            <input
              value={filters.reason}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, reason: e.target.value }))
              }
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              placeholder="Reason"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-600">Sort Direction</label>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={applyFilters}
            className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" /> Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-3 text-sm text-gray-600 flex items-center justify-between">
          <span>{showingText}</span>
          <div className="flex items-center gap-2">
            <span>Per page</span>
            <select
              value={perPage}
              onChange={(e) => setPerPage(Number(e.target.value))}
              className="border border-gray-200 rounded-lg px-2 py-1"
            >
              {[5, 10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">Date Received</th>
                <th className="p-3 text-left">Transaction No</th>
                <th className="p-3 text-left">LOA No</th>
                <th className="p-3 text-left">Provider Code</th>
                <th className="p-3 text-left">Coverage Code</th>
                <th className="p-3 text-left">Claim Status</th>
                <th className="p-3 text-left">Uploaded By</th>
                <th className="p-3 text-left">SOA</th>
                <th className="p-3 text-left">Reason</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan="10" className="p-4 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-4 text-center text-gray-500">
                    No excluded logs found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="p-3">{formatValue(row.date_received)}</td>
                    <td className="p-3">{formatValue(row.trans_no)}</td>
                    <td className="p-3">{formatValue(row.loa_no)}</td>
                    <td className="p-3">{formatValue(row.provider_code)}</td>
                    <td className="p-3">{formatValue(row.coverage_code)}</td>
                    <td className="p-3">{formatValue(row.claim_status)}</td>
                    <td className="p-3">
                      <div className="text-gray-900">
                        {formatValue(row.uploaded_by)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.uploaded_by_id ? `ID: ${row.uploaded_by_id}` : ""}
                      </div>
                    </td>
                    <td className="p-3">{formatValue(row.soa)}</td>
                    <td className="p-3 text-xs text-gray-600 break-words">
                      {formatValue(row.reason)}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => openViewModal(row)}
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-1" /> View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 flex items-center justify-between text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Previous
          </button>
          <span>
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {isModalOpen && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Excluded Log Details
                </h2>
                <p className="text-xs text-gray-500">View full record data</p>
              </div>
              <button
                onClick={closeViewModal}
                className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
              >
                Close
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {detailFields.map((field) => (
                <div key={field.key} className="rounded-lg border border-gray-100 p-3">
                  <div className="text-xs text-gray-500">{field.label}</div>
                  <div className="text-gray-900 break-words">
                    {formatValue(selectedRow[field.key])}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimsReceivingExcludedLogs;
